import { useEffect, useRef, useState, useCallback } from 'react';
import { websocketService } from '@/services/websocket';
import { useAuthStore } from '@/stores/authStore';
import type {
  TableUpdateMessage,
  MenuUpdateMessage,
  PosHeartbeatMessage,
} from '@/types/api';

interface WebSocketHandlers {
  onTableUpdate?: (message: TableUpdateMessage) => void;
  onMenuUpdate?: (message: MenuUpdateMessage) => void;
  onConfigUpdate?: (message: any) => void;
  onHeartbeat?: (message: PosHeartbeatMessage) => void;
}

export function useWebSocket(_handlers: WebSocketHandlers) {
  const isConnecting = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuthStore();

  const connect = useCallback(async () => {
    // Don't connect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    if (isConnecting.current) {
      return;
    }

    // Check if already connected via the service
    if (websocketService.isConnected()) {
      setIsConnected(true);
      return;
    }

    isConnecting.current = true;

    try {
      await websocketService.connect();
      setIsConnected(true);
    } catch {
      setIsConnected(false);
      // Connection failed - will retry automatically
    } finally {
      isConnecting.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      websocketService.disconnect();
      setIsConnected(false);
    }

    return () => {
      setIsConnected(false);
    };
  }, [connect, isAuthenticated]);

  return {
    isConnected,
    reconnect: connect,
  };
}

export function useTableUpdates(
  restaurantId: string,
  onUpdate: (message: TableUpdateMessage) => void
) {
  const { isConnected } = useWebSocket({});

  useEffect(() => {
    if (!isConnected || !restaurantId) {
      return;
    }

    try {
      const unsubscribe = websocketService.subscribeToTableUpdates(
        restaurantId,
        onUpdate
      );

      return () => {
        unsubscribe();
      };
    } catch {
      // WebSocket not ready yet
    }
  }, [isConnected, restaurantId, onUpdate]);

  return { isConnected };
}

export function useMenuUpdates(
  restaurantId: string,
  onUpdate: (message: MenuUpdateMessage) => void
) {
  const { isConnected } = useWebSocket({});

  useEffect(() => {
    if (!isConnected || !restaurantId) {
      return;
    }

    try {
      const unsubscribe = websocketService.subscribeToMenuUpdates(
        restaurantId,
        onUpdate
      );

      return () => {
        unsubscribe();
      };
    } catch {
      // WebSocket not ready yet
    }
  }, [isConnected, restaurantId, onUpdate]);

  return { isConnected };
}

export function useHeartbeats(
  restaurantId: string,
  onHeartbeat: (message: PosHeartbeatMessage) => void
) {
  const { isConnected } = useWebSocket({});

  useEffect(() => {
    if (!isConnected || !restaurantId) {
      return;
    }

    try {
      const unsubscribe = websocketService.subscribeToHeartbeats(restaurantId, onHeartbeat);

      return () => {
        unsubscribe();
      };
    } catch {
      // WebSocket not ready yet
    }
  }, [isConnected, restaurantId, onHeartbeat]);

  return { isConnected };
}

export function useRestaurantUpdates(
  restaurantId: string,
  handlers: {
    onTableUpdate?: (message: TableUpdateMessage) => void;
    onMenuUpdate?: (message: MenuUpdateMessage) => void;
    onConfigUpdate?: (message: any) => void;
  }
) {
  const { isConnected } = useWebSocket({});

  useEffect(() => {
    if (!isConnected || !restaurantId) {
      return;
    }

    try {
      const unsubscribe = websocketService.subscribeToRestaurant(
        restaurantId,
        handlers
      );

      return () => {
        unsubscribe();
      };
    } catch {
      // WebSocket not ready yet
    }
  }, [isConnected, restaurantId, handlers]);

  return { isConnected };
}

export function useInventoryWebSocket(
  restaurantId: string | null,
  onUpdate: (data: any) => void
) {
  const { isConnected } = useWebSocket({});

  useEffect(() => {
    if (!isConnected || !restaurantId) {
      return;
    }

    try {
      const unsubscribe = websocketService.subscribeToInventory(
        restaurantId,
        onUpdate
      );

      return () => {
        unsubscribe();
      };
    } catch {
      // WebSocket not ready yet
    }
  }, [isConnected, restaurantId, onUpdate]);

  return { isConnected };
}
